import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Message from './Message';
import DialogueEditor from '../DialogueEditor/DialogueEditor';
import SceneWorkflow from '../SceneWorkflow/SceneWorkflow';
import VideoGeneratorLayout from '../VideoGenerator/VideoGeneratorLayout';

const MessageList = ({
  messages,
  isLoading,
  projectData,
  currentSceneIndex,
  setCurrentSceneIndex,
  sceneGenerationStatus,
  isGeneratingScene,
  onGenerateScene,
  onApproveScene,
  onRefineScene,
  onFinalizeProject,
  onCreateProject,
  onNewChat,
  onSendMessage,
  resetKey = 0, 
}) => {

const handleLayoutSubmit = async (formData) => {
  if (!onSendMessage) return null;

  return await onSendMessage(
    formData.prompt,
    null,
    null,
    [],
    parseInt(formData.duration, 10),
    formData.scriptMode === 'direct',
    formData.dialogue === 'yes',
    formData.presenter || null,
    formData.aspectRatio || null,
    formData.offer && formData.offer !== 'no-offer'
      ? formData.offer
      : null,
    formData.logoPosition && formData.logoPosition !== 'no-logo'
      ? formData.logoPosition
      : null,
    formData.logoUrl || null,
    formData.referenceImageUrl || null,
    formData.referenceVideoUrl || null,
    formData.languages?.length > 0 ? formData.languages : null,
    formData.companyName || null,
    formData.productImgUrls?.length > 0
      ? formData.productImgUrls
      : null,
    formData.modelImgUrls?.length > 0
      ? formData.modelImgUrls
      : null,
    formData.backgroundUrls?.length > 0
      ? formData.backgroundUrls
      : null,
    null,
    formData.aiModel || 'veo',
    formData.voiceUrl || null,
    formData.referenceImageUrl || null,
    formData.generateThumbnails ?? false,
    formData.hookLine || null,
  );
};

  const sceneWorkflowMsg  = messages.find((m) => m.isSceneWorkflow);
  const dialogueEditorMsg = messages.find((m) => m.isDialogueEditor);

  // ── CASE 1: SceneWorkflow ────────────────────────────────────────────────
  // AFTER
if (sceneWorkflowMsg) {
  const rightPanel = (
    <div className="h-full flex flex-col overflow-y-auto custom-scrollbar">
      <SceneWorkflow
        projectData={projectData}
        sceneGenerationStatus={sceneGenerationStatus}
        isGeneratingScene={isGeneratingScene}
        onGenerateScene={onGenerateScene}
        onApproveScene={onApproveScene}
        onRefineScene={onRefineScene}
        onFinalizeProject={onFinalizeProject}
        currentSceneIndex={currentSceneIndex}
        setCurrentSceneIndex={setCurrentSceneIndex}
        onNewChat={onNewChat}
        aiModel={sceneWorkflowMsg.aiModel}
      />
    </div>
  );
  return (
    <div className="flex items-start w-full min-h-[80vh] pt-4">
      <VideoGeneratorLayout
      key={resetKey}  
        onSubmit={handleLayoutSubmit}
        isLoading={isLoading}
        sceneWorkflow={rightPanel}
      />
    </div>
  );
}

  // ── CASE 2: DialogueEditor ───────────────────────────────────────────────
  if (dialogueEditorMsg) {
    const rightPanel = (
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="px-5 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-sm">
                <span className="text-base">✍️</span>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-800 leading-tight">Scene Dialogues</h3>
                <p className="text-sm text-gray-400 mt-0.5">Review &amp; edit before generating</p>
              </div>
            </div>
            <span className="text-sm font-medium bg-amber-50 text-amber-600 border border-amber-200 px-2 py-1 rounded-lg">
              {dialogueEditorMsg.scenes?.length ?? 0} scenes
            </span>
          </div>
        </div>

        {/* Editor body */}
        <div className="flex-1 overflow-y-auto custom-scrollbar px-3 py-3">
          {dialogueEditorMsg.text && (
            <div className="mb-4 flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-xl p-3">
              <span className="text-blue-500 text-lg mt-0.5 flex-shrink-0">ℹ️</span>
              <p className="text-sm text-blue-700 leading-relaxed">{dialogueEditorMsg.text}</p>
            </div>
          )}
          <DialogueEditor
            scenes={dialogueEditorMsg.scenes}
            onProceedToGeneration={onCreateProject}
            onNewChat={onNewChat}
            aiModel={dialogueEditorMsg.aiModel}
          />
        </div>
      </div>
    );

    return (
      <div className="flex items-start w-full min-h-[80vh] pt-4">
        <VideoGeneratorLayout
        key={resetKey}  
          onSubmit={handleLayoutSubmit}
          isLoading={isLoading}
          sceneWorkflow={rightPanel}
        />
      </div>
    );
  }

  // ── CASE 3: Empty start screen ───────────────────────────────────────────
  if (messages.length === 0) {
    return (
      <div className="flex items-start w-full min-h-[80vh] pt-4">
        <VideoGeneratorLayout
        key={resetKey}  
          onSubmit={handleLayoutSubmit}
          isLoading={isLoading}
        />
      </div>
    );
  }

  // ── CASE 4: Normal messages ──────────────────────────────────────────────
  const botMessages = messages.filter((m) => !m.isUser);

  const rightPanel = (
    <div className="h-full flex flex-col">

      {/* Panel header */}
      <div className="px-5 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-sm">
            <span className="text-base">🤖</span>
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-800 leading-tight">AI Response</h3>
            <p className="text-sm text-gray-400 mt-0.5">
              {isLoading ? 'Generating your script…' : `${botMessages.length} message${botMessages.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          {isLoading && (
            <div className="ml-auto flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          )}
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-5 flex flex-col gap-4">
        <AnimatePresence>

          {/* Loading state */}
          {isLoading && !projectData && (
            <motion.div
              key="loading"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex flex-col gap-3"
            >
              {/* Animated loader card */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="relative w-10 h-10 flex-shrink-0">
                    <div className="absolute inset-0 border-[3px] border-blue-100 rounded-full" />
                    <div className="absolute inset-0 border-[3px] border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <div className="absolute inset-0 flex items-center justify-center text-lg">🎬</div>
                  </div>
                  <div>
                    <p className="text-base font-semibold text-blue-800">Crafting your script…</p>
                    <p className="text-sm text-blue-500 mt-0.5">Usually takes 30–60 seconds</p>
                  </div>
                </div>

                {/* Skeleton lines */}
                <div className="space-y-2.5">
                  {[100, 85, 92, 70].map((w, i) => (
                    <div
                      key={i}
                      className="h-2.5 bg-blue-200/60 rounded-full animate-pulse"
                      style={{ width: `${w}%`, animationDelay: `${i * 120}ms` }}
                    />
                  ))}
                </div>

                {/* Progress dots */}
                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-blue-100">
                  {['Analysing prompt', 'Building scenes', 'Crafting dialogues'].map((step, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-sm text-blue-600">
                      <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: `${i * 200}ms` }} />
                      {step}
                      {i < 2 && <span className="text-blue-300 mx-1">·</span>}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Bot messages */}
          {botMessages.map((message, i) => {
            if (message.text && typeof message.text === 'object' && !React.isValidElement(message.text)) {
              return null;
            }

            // Video ready
            if (message.isVideo && message.videoUrl) {
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 28 }}
                  className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 rounded-2xl overflow-hidden"
                >
                  <div className="px-5 py-4 border-b border-emerald-100 flex items-center gap-2.5">
                    <span className="text-2xl">🎬</span>
                    <div>
                      <p className="text-base font-bold text-emerald-800">Your Video is Ready!</p>
                      <p className="text-sm text-emerald-500">Click to play or download</p>
                    </div>
                  </div>
                  <div className="p-4 space-y-3">
                    <video controls className="w-full rounded-xl shadow-sm border border-emerald-100 bg-black">
                      <source src={message.videoUrl} type="video/mp4" />
                    </video>
                    <a
                      href={message.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-base font-semibold transition-colors shadow-sm"
                    >
                      <span>🔗</span> Open Full Video
                    </a>
                  </div>
                </motion.div>
              );
            }

            // Regular bot message
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04, type: 'spring', stiffness: 300, damping: 30 }}
                className="bg-teal-50 border border-gray-100 rounded-2xl shadow-sm overflow-hidden"
              >
                {/* Message header */}
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm">✨</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">AI Response</span>
                  <span className="ml-auto text-sm text-gray-400">#{i + 1}</span>
                </div>

                {/* Message body */}
                <div className="px-4 py-4">
                  <Message message={message} index={i} />
                </div>
              </motion.div>
            );
          })}

          {/* Empty state */}
          {!isLoading && botMessages.length === 0 && (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center h-48 text-center gap-3"
            >
              <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
                <span className="text-3xl">⏳</span>
              </div>
              <div>
                <p className="text-base font-semibold text-gray-500">Waiting for response</p>
                <p className="text-sm text-gray-400 mt-1">Your AI-generated content will appear here</p>
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );

  return (
    <div className="flex items-start w-full min-h-[80vh] pt-4">
      <VideoGeneratorLayout
      key={resetKey}  
        onSubmit={handleLayoutSubmit}
        isLoading={isLoading}
        sceneWorkflow={rightPanel}
      />
    </div>
  );
};

export default MessageList;
